import type { DashboardDataset, SalesRecord, TargetConfig, TerminalRow } from '../types';
import type { PenetrationRow } from './analytics';
import { sanitizeFileName } from './format';

type ExportValue = string | number | boolean | null;
type ExportRow = Record<string, ExportValue>;

const widths = (rows: ExportRow[]) => {
  const keys = Object.keys(rows[0] ?? {});
  return keys.map((key) => ({
    wch: Math.min(42, Math.max(key.length * 2 + 2, ...rows.slice(0, 300).map((row) => String(row[key] ?? '').length + 2))),
  }));
};

const writeWorkbook = async (fileName: string, sheets: Array<{ name: string; rows: ExportRow[] }>) => {
  const XLSX = await import('xlsx');
  const workbook = XLSX.utils.book_new();
  sheets.forEach(({ name, rows }) => {
    const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ 说明: '当前筛选暂无数据' }]);
    sheet['!cols'] = widths(rows);
    sheet['!autofilter'] = sheet['!ref'] ? { ref: sheet['!ref'] } : undefined;
    XLSX.utils.book_append_sheet(workbook, sheet, name.slice(0, 31));
  });
  XLSX.writeFile(workbook, `${sanitizeFileName(fileName)}.xlsx`, { compression: true });
};

const detailRows = (records: SalesRecord[]): ExportRow[] =>
  records.map((record) => ({
    销售ID: record.salesId,
    产品: record.product,
    品牌: record.brand,
    CE: record.businessUnit,
    数量: record.quantity,
    '金额(内部考核价)': record.internalAmount,
    销售时间: record.saleDate,
    '进货方代码(汇总)': record.customerCode,
    '进货方名称(汇总)': record.customerName,
    进货方省份: record.province,
    进货方城市: record.city,
    进货方类型: record.customerType,
    流向类型: record.flowType,
    创建日期: record.createdAt,
    MCE大区: record.regionManager,
    PM: record.pm,
    经销商名称: record.distributor,
    是否罗盘: record.compass,
    业务类型: record.movementType,
    出厂价金额: record.factoryAmount,
  }));

export const exportDetails = (records: SalesRecord[], dateLabel: string) =>
  writeWorkbook(`销售明细_${dateLabel}`, [{ name: '筛选后明细', rows: detailRows(records) }]);

export const exportTerminals = (rows: TerminalRow[], dateLabel: string) =>
  writeWorkbook(`终端分析_${dateLabel}`, [{
    name: '终端分析',
    rows: rows.map((row) => ({
      终端代码: row.customerCode,
      终端名称: row.customerName,
      省份: row.province,
      城市: row.city,
      流向类型: row.flowType,
      销售额: row.sales,
      销售数量: row.quantity,
      覆盖产品数: row.productCount,
      最近采购日: row.lastPurchase,
      目标单品是否渗透: row.hasTargetProduct ? '是' : '否',
    })),
  }]);

export const exportPenetration = (rows: PenetrationRow[], targetProduct: string, dateLabel: string) =>
  writeWorkbook(`单品渗透_${dateLabel}`, [{
    name: '单品渗透',
    rows: rows.map((row) => ({
      目标单品: targetProduct,
      省份: row.province,
      城市: row.city,
      活跃终端数: row.totalTerminals,
      已渗透终端数: row.targetTerminals,
      未渗透终端数: row.gap,
      渗透率: row.penetration / 100,
      单品销售额: row.targetSales,
      总销售额: row.totalSales,
    })),
  }]);

export interface KpiExportRow {
  period: string;
  sales: number;
  salesTarget: number;
  productQuantity: number;
  productTarget: number;
  terminals: number;
  terminalTarget: number;
}

export const exportKpis = (rows: KpiExportRow[], targets: TargetConfig, dateLabel: string) =>
  writeWorkbook(`KPI跟进_${dateLabel}`, [
    {
      name: 'KPI跟进',
      rows: rows.map((row) => ({
        周期: row.period,
        销售额: row.sales,
        销售额目标: row.salesTarget,
        销售额达成率: row.salesTarget ? row.sales / row.salesTarget : 0,
        目标单品数量: row.productQuantity,
        目标单品数量目标: row.productTarget,
        单品达成率: row.productTarget ? row.productQuantity / row.productTarget : 0,
        活跃终端: row.terminals,
        活跃终端目标: row.terminalTarget,
        终端达成率: row.terminalTarget ? row.terminals / row.terminalTarget : 0,
      })),
    },
    {
      name: '目标设置',
      rows: [{
        目标单品: targets.targetProduct,
        年度销售额目标: targets.annualSalesTarget,
        年度单品数量目标: targets.annualProductQuantityTarget,
        活跃终端目标: targets.activeTerminalTarget,
        更新时间: targets.updatedAt,
      }],
    },
  ]);

export const exportFullPackage = async (
  dataset: DashboardDataset,
  records: SalesRecord[],
  terminals: TerminalRow[],
  penetration: PenetrationRow[],
  targets: TargetConfig,
) => {
  const dateLabel = `${dataset.meta.dateMin}_${dataset.meta.dateMax}`;
  await writeWorkbook(`业务看板分析包_${dateLabel}`, [
    { name: '销售明细', rows: detailRows(records) },
    {
      name: '终端分析',
      rows: terminals.map((row) => ({
        终端代码: row.customerCode,
        终端名称: row.customerName,
        省份: row.province,
        城市: row.city,
        流向类型: row.flowType,
        销售额: row.sales,
        销售数量: row.quantity,
        覆盖产品数: row.productCount,
        最近采购日: row.lastPurchase,
        目标单品是否渗透: row.hasTargetProduct ? '是' : '否',
      })),
    },
    {
      name: '单品渗透',
      rows: penetration.map((row) => ({
        目标单品: targets.targetProduct,
        省份: row.province,
        城市: row.city,
        活跃终端数: row.totalTerminals,
        已渗透终端数: row.targetTerminals,
        渗透率: row.penetration / 100,
        单品销售额: row.targetSales,
      })),
    },
    {
      name: '数据说明',
      rows: [{
        来源文件: dataset.meta.fileName,
        工作表: dataset.meta.sheetName,
        导入时间: dataset.meta.importedAt,
        数据起始日: dataset.meta.dateMin,
        数据截止日: dataset.meta.dateMax,
        导入记录数: dataset.meta.rowCount,
        隐私说明: '原始 Excel 仅在浏览器本地解析，本文件由当前浏览器生成',
      }],
    },
  ]);
};
